import { CatalogProductWithInventory } from "@redotech/marketplace-sdk/rpc/schema/get-marketplace-products";
import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMarketplaceRpcClientContext } from "@redotech/redo-marketplace-app-common/rpc-client";
import { VendorAuthContext } from "@redotech/redo-marketplace-app-common/vendor-auth";
import { CatalogVariant } from "@redotech/redo-model/catalog/catalog-product-types";
import {
  MarketplaceCartItem,
  MarketplaceCartItemWithProduct,
} from "@redotech/redo-model/recommerce/marketplace-cart";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { memo, useCallback, useContext, useMemo, useState } from "react";
import { CartContext, CartContextType } from "./cart-context";

interface CartProviderProps {
  children: React.ReactNode;
}

function calculateTotalPrice(
  cartItems: readonly MarketplaceCartItemWithProduct[],
): number {
  return cartItems.reduce((total, cartItem) => {
    if (!cartItem.variant || !cartItem.variant.price) {
      return total;
    }
    return total + cartItem.variant.price * cartItem.quantity;
  }, 0);
}

export const CartProvider = memo(function CartProvider({
  children,
}: CartProviderProps) {
  const vendorAuth = useContext(VendorAuthContext);
  const marketplaceRpcClient = useRequiredContext(
    RedoMarketplaceRpcClientContext,
  );
  const queryClient = useQueryClient();
  const [itemErrors, setItemErrors] = useState<Map<string, string>>(new Map());
  const [updatingItemId, setUpdatingItemId] = useState<string | null>(null);

  // Load cart from server using React Query
  const {
    data: cartData,
    isPending,
    refetch,
  } = useQuery({
    queryKey: ["cart", vendorAuth?.vendorId],
    queryFn: async () => {
      return await marketplaceRpcClient.getCart(undefined);
    },
    enabled: !!vendorAuth?.vendorId,
  });

  const items: readonly MarketplaceCartItemWithProduct[] = useMemo(() => {
    if (!cartData?.cart?.items) {
      return [];
    }

    return cartData.cart.items;
  }, [cartData?.cart?.items]);

  const getItem = useCallback(
    (productId: string, variantId: string): MarketplaceCartItem | undefined => {
      return items.find(
        (item) => item.productId === productId && item.variantId === variantId,
      );
    },
    [items],
  );

  const addItemMutation = useMutation({
    mutationFn: async (params: {
      product: CatalogProductWithInventory;
      variant: CatalogVariant;
      quantity: number;
    }) => {
      if (!vendorAuth?.vendorId) {
        throw new Error("Vendor not authenticated");
      }

      return await marketplaceRpcClient.addToCart({
        productId: "",
        variantId: params.variant._id,
        quantity: params.quantity,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["cart", vendorAuth?.vendorId],
      });
    },
    onError: (error: any) => {
      console.error("Add to cart failed:", error);
      // Handle add to cart errors separately if needed
    },
  });

  const removeItemMutation = useMutation({
    mutationFn: async (params: { productId: string; variantId: string }) => {
      if (!vendorAuth?.vendorId) {
        throw new Error("Vendor not authenticated");
      }

      return await marketplaceRpcClient.removeFromCart(params);
    },
    onMutate: async (params) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: ["cart", vendorAuth?.vendorId],
      });

      // Snapshot the previous value
      const previousCart = queryClient.getQueryData([
        "cart",
        vendorAuth?.vendorId,
      ]);

      // Optimistically update the cache
      queryClient.setQueryData(["cart", vendorAuth?.vendorId], (old: any) => {
        if (!old?.cart?.items) {
          return old;
        }

        return {
          ...old,
          cart: {
            ...old.cart,
            items: old.cart.items.filter(
              (item: MarketplaceCartItem) =>
                !(
                  item.productId === params.productId &&
                  item.variantId === params.variantId
                ),
            ),
          },
        };
      });

      // Return context with snapshot for rollback
      return { previousCart };
    },
    onError: (error: any, params, context: any) => {
      // Rollback to previous state on error
      if (context?.previousCart) {
        queryClient.setQueryData(
          ["cart", vendorAuth?.vendorId],
          context.previousCart,
        );
      }
      console.error("Remove from cart failed:", error);
    },
    onSettled: async () => {
      // Always refetch after mutation to ensure sync with server
      await queryClient.invalidateQueries({
        queryKey: ["cart", vendorAuth?.vendorId],
      });
    },
  });

  const updateQuantityMutation = useMutation({
    mutationFn: async (params: {
      productId: string;
      variantId: string;
      quantity: number;
    }) => {
      if (!vendorAuth?.vendorId) {
        throw new Error("Vendor not authenticated");
      }

      const item = getItem(params.productId, params.variantId);

      if (!item) {
        throw new Error(
          `Item not found. ProductId: ${params.productId}, VariantId: ${params.variantId}`,
        );
      }

      return await marketplaceRpcClient.updateCartItem(params);
    },
    onMutate: async (params) => {
      const itemId = `${params.productId}-${params.variantId}`;
      setUpdatingItemId(itemId);

      // Clear any previous error for this item
      setItemErrors((prev) => {
        const next = new Map(prev);
        next.delete(itemId);
        return next;
      });
    },
    onSuccess: async (data) => {
      setUpdatingItemId(null);
      await queryClient.invalidateQueries({
        queryKey: ["cart", vendorAuth?.vendorId],
      });
    },
    onError: (
      error: any,
      params: { productId: string; variantId: string; quantity: number },
    ) => {
      console.error("Update quantity failed:", error);
      const itemId = `${params.productId}-${params.variantId}`;
      setItemErrors((prev) => {
        const next = new Map(prev);
        next.set(itemId, error?.message || "Unable to update quantity");
        return next;
      });
      setUpdatingItemId(null);
    },
  });

  const clearCartMutation = useMutation({
    mutationFn: async () => {
      if (!vendorAuth?.vendorId) {
        throw new Error("Vendor not authenticated");
      }

      return await marketplaceRpcClient.clearCart();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["cart", vendorAuth?.vendorId],
      });
    },
  });

  const addItem = useCallback(
    async (
      product: CatalogProductWithInventory,
      variant: CatalogVariant,
      quantity = 1,
    ) => {
      try {
        await addItemMutation.mutateAsync({ product, variant, quantity });
      } catch (error) {
        console.error("Failed to add item to cart:", error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addItemMutation.mutateAsync],
  );

  const removeItem = useCallback(
    async (productId: string, variantId: string) => {
      try {
        await removeItemMutation.mutateAsync({ productId, variantId });
      } catch (error) {
        console.error("Failed to remove item from cart:", error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [removeItemMutation.mutateAsync],
  );

  const updateQuantity = useCallback(
    async (productId: string, variantId: string, quantity: number) => {
      if (quantity <= 0) {
        await removeItem(productId, variantId);
        return;
      }

      try {
        await updateQuantityMutation.mutateAsync({
          productId,
          variantId,
          quantity,
        });
      } catch (error) {
        console.error("Failed to update cart item quantity:", error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [removeItem, updateQuantityMutation.mutateAsync],
  );

  const clearCart = useCallback(async () => {
    try {
      await clearCartMutation.mutateAsync();
    } catch (error) {
      console.error("Failed to clear cart:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearCartMutation.mutateAsync]);

  const refreshCart = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const isItemUpdating = useCallback(
    (productId: string, variantId: string) => {
      return updatingItemId === `${productId}-${variantId}`;
    },
    [updatingItemId],
  );

  const getItemError = useCallback(
    (productId: string, variantId: string) => {
      return itemErrors.get(`${productId}-${variantId}`);
    },
    [itemErrors],
  );

  const clearItemError = useCallback((productId: string, variantId: string) => {
    setItemErrors((prev) => {
      const next = new Map(prev);
      next.delete(`${productId}-${variantId}`);
      return next;
    });
  }, []);

  const contextValue: CartContextType = useMemo(
    () => ({
      items,
      totalItems: items.reduce((total, item) => total + item.quantity, 0),
      totalPrice: calculateTotalPrice(items),
      isLoading:
        isPending ||
        addItemMutation.isPending ||
        removeItemMutation.isPending ||
        clearCartMutation.isPending,
      cartId: cartData?.cart?._id,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      refreshCart,
      getItem,
      isItemUpdating,
      getItemError,
      clearItemError,
    }),
    [
      items,
      isPending,
      addItemMutation.isPending,
      removeItemMutation.isPending,
      clearCartMutation.isPending,
      cartData?.cart?._id,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      refreshCart,
      getItem,
      isItemUpdating,
      getItemError,
      clearItemError,
    ],
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
});
